import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { CrmConfig, ActiveAgent, formatNameUrl, formatPhoneUrl, formatDisplayHtml } from './crmUtils';

interface CrmFloatingOverlaysProps {
  showLargeQr: boolean;
  setShowLargeQr: (show: boolean) => void;
  showAgentModal: boolean;
  setShowAgentModal: (show: boolean) => void;
  callLogsSettingsOpen: boolean;
  setCallLogsSettingsOpen: (open: boolean) => void;
  showHeaderMobile: boolean;
  setShowHeaderMobile: (show: boolean) => void;
  toastQueue: any[];
  setToastQueue: React.Dispatch<React.SetStateAction<any[]>>;
  isLight: boolean;
  crm?: CrmConfig;
  activeAgents: ActiveAgent[];
  now: number;
  timeSkew: number;
  clientMaxRows: number | null;
  setClientMaxRows: (n: number | null) => void;
  clientHotCallWindow: number | null;
  setClientHotCallWindow: (n: number | null) => void;
  clientHotCallToastsEnabled: boolean;
  setClientHotCallToastsEnabled: (enabled: boolean) => void;
  defaultChatDid: string;
  setActiveTab: (tab: 'logs' | 'chat' | 'cdr' | 'voicemails') => void;
  setChatTrigger: (trigger: { contact: string; did?: string; ts: number; autoLookup?: boolean }) => void;
  hotCallWindow: number;
  onEditContact: (phone: string, name: string) => void;
}

export function CrmFloatingOverlays({
  showLargeQr,
  setShowLargeQr,
  showAgentModal,
  setShowAgentModal,
  callLogsSettingsOpen,
  setCallLogsSettingsOpen,
  showHeaderMobile,
  setShowHeaderMobile,
  toastQueue,
  setToastQueue,
  isLight,
  crm,
  activeAgents,
  now,
  timeSkew,
  clientMaxRows,
  setClientMaxRows,
  clientHotCallWindow,
  setClientHotCallWindow,
  clientHotCallToastsEnabled,
  setClientHotCallToastsEnabled,
  defaultChatDid,
  setActiveTab,
  setChatTrigger,
  hotCallWindow,
  onEditContact
}: CrmFloatingOverlaysProps) {
  return (
    <>
      {/* Large QR Modal */}
      {showLargeQr && (
        <div
          onClick={() => setShowLargeQr(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.75)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(4px)'
          }}
        >
          <div
            style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1.5rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <QRCodeSVG value={typeof window !== 'undefined' ? window.location.href : ''} size={256} level="M" />
            <p style={{ margin: 0, color: '#09090b', fontWeight: 600, fontSize: '1.1rem' }}>Scan to Open Dashboard</p>
            <button
              onClick={() => setShowLargeQr(false)}
              style={{
                padding: '0.5rem 2rem',
                background: '#f3f4f6',
                color: '#1f2937',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Floating Toasts */}
      {toastQueue.length > 0 && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 99999, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {toastQueue.map(toast => {
            let rawName = toast.responsePayload || '-';
            try {
              const p = JSON.parse(toast.responsePayload);
              if (p.name) rawName = p.name;
            } catch {}

            const urlName = formatNameUrl(rawName, crm?.nameUrlFormat || '');
            const urlPhone = formatPhoneUrl(toast.targetNumber, crm?.phoneUrlFormat || '');

            return (
              <div
                key={toast.id}
                onClick={() => {
                  if (crm?.enableChatClient) {
                    setChatTrigger({ contact: toast.targetNumber, did: defaultChatDid, ts: Date.now(), autoLookup: true });
                    setActiveTab('chat');
                  } else {
                    setActiveTab('logs');
                  }
                  setShowHeaderMobile(false);
                  setToastQueue(q => q.filter(t => t.id !== toast.id));
                }}
                style={{
                  position: 'relative',
                  background: isLight ? 'rgba(255,255,255,0.95)' : 'rgba(24, 24, 27, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  padding: '1rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  minWidth: '250px',
                  animation: 'slideIn 0.3s ease-out'
                }}
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setToastQueue(q => q.filter(t => t.id !== toast.id));
                  }}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    cursor: 'pointer',
                    color: isLight ? '#9ca3af' : '#71717a',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.color = isLight ? '#111827' : '#f4f4f5';
                    e.currentTarget.style.background = isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.color = isLight ? '#9ca3af' : '#71717a';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </div>
                <style dangerouslySetInnerHTML={{ __html: `@keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }` }} />
                {toast.source === 'chat-message' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <div style={{ fontWeight: 600, color: isLight ? '#09090b' : 'white', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                      New Message
                    </div>
                    <div style={{ color: isLight ? '#4b5563' : '#a1a1aa', fontSize: '0.9rem' }}>
                      From: {rawName !== '-' ? rawName : toast.targetNumber}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: 'bold', marginBottom: '0.5rem', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                      Incoming Call
                    </div>
                    <div onClick={e => e.stopPropagation()}>
                      {formatDisplayHtml(rawName, toast.targetNumber, crm, urlName, urlPhone, toast.createdAt, isLight, hotCallWindow, false, false, false, onEditContact)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Mobile Header Toggle (Top Center) */}
      {typeof window !== 'undefined' && window.innerWidth <= 1024 && (
        <button
          onClick={() => setShowHeaderMobile(!showHeaderMobile)}
          title={showHeaderMobile ? "Hide Navigation" : "Show Navigation"}
          style={{
            position: 'fixed',
            top: '4px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            background: 'transparent',
            border: 'none',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: isLight ? 'rgba(9, 9, 11, 0.5)' : 'rgba(255, 255, 255, 0.4)',
            transition: 'all 0.2s ease',
            padding: 0
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = isLight ? 'rgba(9, 9, 11, 0.9)' : 'rgba(255, 255, 255, 0.8)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = isLight ? 'rgba(9, 9, 11, 0.5)' : 'rgba(255, 255, 255, 0.4)'; }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: showHeaderMobile ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
      )}

      {/* Call Logs Settings Modal */}
      {callLogsSettingsOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setCallLogsSettingsOpen(false)}
        >
          <div
            style={{
              background: isLight ? 'white' : '#18181b',
              padding: '1.5rem',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '400px',
              border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', color: isLight ? '#09090b' : 'white', fontWeight: 600 }}>Call Logs Settings</h3>
              <button
                onClick={() => setCallLogsSettingsOpen(false)}
                style={{ background: 'transparent', border: 'none', color: isLight ? '#6b7280' : '#a1a1aa', cursor: 'pointer', display: 'flex' }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: isLight ? '#4b5563' : '#a1a1aa' }}>Max Calls Per Page</label>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={clientMaxRows !== null ? clientMaxRows : (crm?.maxRows || 10)}
                  onChange={(e) => {
                    const v = parseInt(e.target.value);
                    if (!isNaN(v)) {
                      setClientMaxRows(v);
                      localStorage.setItem('crm-max-rows', v.toString());
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: isLight ? '#f9fafb' : 'rgba(0,0,0,0.2)',
                    color: isLight ? '#09090b' : 'white',
                    border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '6px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: isLight ? '#4b5563' : '#a1a1aa' }}>Hot Call Indicator Time (Minutes)</label>
                <input
                  type="number"
                  min="0"
                  value={clientHotCallWindow !== null ? clientHotCallWindow : (crm?.hotCallWindow || 0)}
                  onChange={(e) => {
                    const v = parseInt(e.target.value);
                    if (!isNaN(v)) {
                      setClientHotCallWindow(v);
                      localStorage.setItem('crm-hot-window', v.toString());
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: isLight ? '#f9fafb' : 'rgba(0,0,0,0.2)',
                    color: isLight ? '#09090b' : 'white',
                    border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '6px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.75rem' }}>
                  <div
                    style={{
                      position: 'relative',
                      width: '36px',
                      height: '20px',
                      background: clientHotCallToastsEnabled ? '#3b82f6' : (isLight ? '#e4e4e7' : '#3f3f46'),
                      borderRadius: '10px',
                      transition: 'background 0.2s'
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: '2px',
                        left: clientHotCallToastsEnabled ? '18px' : '2px',
                        width: '16px',
                        height: '16px',
                        background: 'white',
                        borderRadius: '50%',
                        transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                      }}
                    />
                  </div>
                  <input
                    type="checkbox"
                    checked={clientHotCallToastsEnabled}
                    onChange={(e) => {
                      setClientHotCallToastsEnabled(e.target.checked);
                      localStorage.setItem('crm-hot-call-toasts', e.target.checked.toString());
                    }}
                    style={{ display: 'none' }}
                  />
                  <span style={{ fontSize: '0.9rem', color: isLight ? '#4b5563' : '#a1a1aa' }}>Show Hot Caller Toast Notifications</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Agents Modal */}
      {showAgentModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => setShowAgentModal(false)}
        >
          <div
            style={{
              background: isLight ? 'white' : '#18181b',
              border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '16px',
              width: '90%',
              maxWidth: '400px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '1rem 1.5rem', borderBottom: isLight ? '1px solid #f3f4f6' : '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: isLight ? '#09090b' : 'white', fontWeight: 600 }}>Active Agents</h3>
              <button
                onClick={() => setShowAgentModal(false)}
                style={{ background: 'transparent', border: 'none', color: isLight ? '#6b7280' : '#a1a1aa', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.2rem' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div style={{ padding: '1rem', maxHeight: '60vh', overflowY: 'auto' }}>
              {activeAgents.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: isLight ? '#6b7280' : '#a1a1aa', fontSize: '0.9rem' }}>No agents are currently active.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {activeAgents.sort((a, b) => b.lastActive - a.lastActive).map((agent, i) => {
                    const adjustedNow = now - timeSkew;
                    const isIdle = agent.status ? agent.status === 'inactive' : (adjustedNow - agent.lastActive) > 60000;
                    const idleMs = Math.max(0, adjustedNow - agent.lastActive);
                    const idleMins = Math.floor(idleMs / 60000);
                    return (
                      <div
                        key={agent.name + i}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.75rem',
                          background: isLight ? '#f9fafb' : 'rgba(0,0,0,0.2)',
                          border: isLight ? '1px solid #f3f4f6' : '1px solid rgba(255,255,255,0.05)',
                          borderRadius: '8px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0, flex: 1 }}>
                          <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: isIdle ? '#fbbf24' : '#22c55e', display: 'inline-block', boxShadow: isIdle ? 'none' : '0 0 6px #22c55e' }}></span>
                          <span style={{ fontWeight: 600, color: isLight ? '#1f2937' : '#f4f4f5', fontSize: '0.95rem' }}>{agent.name}</span>
                        </div>
                        <span style={{ fontSize: '0.8rem', color: isLight ? '#6b7280' : '#a1a1aa' }}>
                          {isIdle ? `Idle for ${idleMins}m` : 'Active'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
